/**
 * Forecast-Editor (#1857) — eingebettet im Plan-Editor als Ebene "Forecast".
 *
 * Je Zelle: Plan (read-only, grau) + Forecast (editierbar) + Abweichung.
 * Leeres Forecast-Feld = der Plan gilt (Loeschen der aktuellen Version).
 * "Stand vom": historischer Forecast-Stand, dann strikt read-only (AK3).
 * Teil B: Mengenplanung (Pakete, Wareneingaenge) + Treiber (Menge x EUR/Einheit)
 * + IST-Kennzahl EUR je Einheit + Plausibilitaetshinweise.
 */
import { useEffect, useMemo, useState } from 'react';

const API = import.meta.env.VITE_API_URL || '';
const MONTHS = ['Jan','Feb','Mär','Apr','Mai','Jun','Jul','Aug','Sep','Okt','Nov','Dez'];
const MENGE_LABELS = { menge_pakete: 'Pakete', menge_wareneingaenge: 'Wareneingänge' };

function fmtEur(v, digits = 0) {
  if (v === null || v === undefined || v === '') return '–';
  const n = parseFloat(v);
  if (!isFinite(n)) return '–';
  return new Intl.NumberFormat('de-AT', { maximumFractionDigits: digits }).format(n);
}
function labelize(p) {
  return p.replace(/^guv_|^bilanz_|^cashflow_|^annahmen_/, '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

export default function InvestorForecastEditor({ year, bereich }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [stand, setStand] = useState('');
  const [editKey, setEditKey] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [saving, setSaving] = useState(false);
  const [historie, setHistorie] = useState(null); // { position, monat, versionen }
  const [kennzahlen, setKennzahlen] = useState({});
  const [tf, setTf] = useState({ position: '', menge_position: 'menge_pakete', satz_eur: '' });

  const readOnly = !!stand;

  async function load() {
    setLoading(true);
    try {
      const params = new URLSearchParams({ jahr: String(year), bereich });
      if (stand) params.set('stand', stand);
      const res = await fetch(`${API}/api/investor/forecast?${params}`, { credentials: 'include' });
      if (res.ok) setData(await res.json());
    } finally { setLoading(false); }
  }
  useEffect(() => { load(); }, [year, bereich, stand]); // eslint-disable-line

  // IST-EUR je Einheit fuer jede Treiber-Position (AK4)
  useEffect(() => {
    if (!data?.treiber) return;
    (async () => {
      const out = {};
      for (const [pos, t] of Object.entries(data.treiber)) {
        const params = new URLSearchParams({ position: pos, menge_position: t.menge_position });
        const res = await fetch(`${API}/api/investor/forecast/kennzahlen?${params}`, { credentials: 'include' });
        if (res.ok) out[pos] = await res.json();
      }
      setKennzahlen(out);
    })();
  }, [data]); // eslint-disable-line

  const grid = useMemo(() => {
    if (!data) return [];
    const map = new Map();
    for (const r of data.rows) {
      if (!map.has(r.position)) map.set(r.position, { position: r.position, cells: {} });
      map.get(r.position).cells[r.monat] = r;
    }
    return Array.from(map.values()).sort((a, b) => a.position.localeCompare(b.position));
  }, [data]);

  const mengen = useMemo(() => {
    const m = { menge_pakete: {}, menge_wareneingaenge: {} };
    for (const row of data?.mengen || []) m[row.position][row.monat] = row.menge;
    return m;
  }, [data]);

  async function api(method, path, body) {
    setSaving(true);
    try {
      const res = await fetch(`${API}${path}`, {
        method,
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: body ? JSON.stringify(body) : undefined,
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        alert('Fehler: ' + (d.error || res.status));
        return false;
      }
      return true;
    } finally { setSaving(false); }
  }

  async function saveZelle(position, monat) {
    const leer = editValue === '';
    const ok = leer
      ? await api('DELETE', '/api/investor/forecast/wert', { jahr: year, monat, position })
      : await api('PUT', '/api/investor/forecast/wert', { jahr: year, monat, position, wert_eur: parseFloat(editValue) });
    if (ok) { setEditKey(null); await load(); }
  }

  async function zeigeHistorie(position, monat) {
    const params = new URLSearchParams({ jahr: String(year), monat: String(monat), position });
    const res = await fetch(`${API}/api/investor/forecast/historie?${params}`, { credentials: 'include' });
    if (res.ok) {
      const d = await res.json();
      setHistorie({ position, monat, versionen: d.versionen || [] });
    }
  }

  async function speichereTreiber() {
    if (!tf.position || tf.satz_eur === '') return;
    const ok = await api('PUT', '/api/investor/forecast/treiber', {
      position: tf.position, menge_position: tf.menge_position, satz_eur: parseFloat(tf.satz_eur), aktiv: true,
    });
    if (ok) { setTf({ position: '', menge_position: 'menge_pakete', satz_eur: '' }); await load(); }
  }

  function Zelle({ position, monat, cell }) {
    const k = `${position}|${monat}`;
    const isEditing = editKey === k;
    const plan = cell ? parseFloat(cell.plan_eur) : null;
    const fc = cell && cell.forecast_eur !== null ? parseFloat(cell.forecast_eur) : null;
    const abw = plan !== null && fc !== null ? fc - plan : null;
    return (
      <td className="px-2 py-1 text-right tabular-nums text-xs align-top">
        {!cell ? <span className="text-[#E5E7EB]">–</span> : (
          <div>
            <div className="text-[10px] text-[#9CA3AF]">{fmtEur(plan)}</div>
            {isEditing ? (
              <input
                type="number" value={editValue} autoFocus disabled={saving}
                onChange={e => setEditValue(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter') saveZelle(position, monat);
                  if (e.key === 'Escape') setEditKey(null);
                }}
                onBlur={() => saveZelle(position, monat)}
                className="w-20 px-1 py-0.5 border border-[#338edc] rounded text-right text-xs"
              />
            ) : (
              <button
                disabled={readOnly}
                onClick={() => { setEditKey(k); setEditValue(fc ?? ''); }}
                onContextMenu={e => { e.preventDefault(); zeigeHistorie(position, monat); }}
                title={cell.forecast_art === 'treiber' ? 'Aus Treiber (Menge × Satz) — Rechtsklick: Historie' : 'Rechtsklick: Historie'}
                className={fc === null
                  ? 'text-[#C4C9D0] hover:text-[#338edc]'
                  : cell.forecast_art === 'treiber'
                    ? 'text-[#1A1A2E] italic hover:text-[#338edc]'
                    : 'text-[#1A1A2E] font-medium hover:text-[#338edc]'}
              >
                {fc === null ? '=' : fmtEur(fc)}
              </button>
            )}
            {abw !== null && Math.round(abw) !== 0 && (
              <div className="text-[10px] text-[#6B7280]">
                {abw > 0 ? '+' : ''}{fmtEur(abw)} ({plan !== 0 ? `${abw > 0 ? '+' : ''}${(abw / Math.abs(plan) * 100).toFixed(1)}%` : '–'})
              </div>
            )}
            {cell.ist_eur !== null && (
              <div className="text-[10px] text-[#338edc]" title="IST aus Odoo">I {fmtEur(cell.ist_eur)}</div>
            )}
          </div>
        )}
      </td>
    );
  }

  const hinweise = data?.plausibilitaet?.hinweise || [];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="text-xs text-[#6B7280]">
          Grau = Plan (eingefroren) · Wert = Forecast · „=" = leer, der Plan gilt · kursiv = aus Treiber · blau I = Ist (Odoo) · Rechtsklick = Versionshistorie
        </div>
        <div className="flex items-center gap-2">
          <label className="text-xs text-[#6B7280]">Stand vom</label>
          <input
            type="date" value={stand} onChange={e => setStand(e.target.value)}
            className="text-xs border border-[#E5E7EB] rounded-md px-2 py-1.5 bg-white"
          />
          {stand && (
            <button onClick={() => setStand('')} className="text-xs text-[#338edc] hover:underline">aktuell</button>
          )}
        </div>
      </div>

      {readOnly && (
        <div className="text-xs px-3 py-2 rounded-lg border border-[#FDE68A] bg-[#FFFBEB] text-[#92400E]">
          Historischer Forecast-Stand vom {stand} — nur Ansicht, Änderungen sind hier nicht möglich.
        </div>
      )}

      {hinweise.length > 0 && (
        <div className="border border-[#FDE68A] bg-[#FFFBEB] rounded-xl px-4 py-3 space-y-1">
          <div className="text-xs font-semibold text-[#92400E]">
            Plausibilität: Kosten je Einheit weichen &gt; {Math.round((data.plausibilitaet.schwelle || 0.1) * 100)} % vom 3-Monats-Ist ab
          </div>
          {hinweise.map((h, i) => (
            <div key={i} className="text-xs text-[#92400E]">
              {labelize(h.position)} {h.monat}: {fmtEur(h.eur_pro_einheit, 2)} €/Einheit ({h.wert_herkunft}, {fmtEur(h.wert_eur)} € bei {fmtEur(h.menge)} {MENGE_LABELS[h.menge_position]}) vs. Ist-Schnitt {fmtEur(h.ist_schnitt_3m, 2)} €/Einheit → {h.abweichung_prozent > 0 ? '+' : ''}{h.abweichung_prozent} %
            </div>
          ))}
        </div>
      )}

      <div className="bg-white border border-[#E5E7EB] rounded-xl overflow-x-auto">
        <table className="text-sm min-w-full">
          <thead className="bg-[#F8F9FC] border-b border-[#E5E7EB]">
            <tr>
              <th className="text-left px-3 py-2 text-[11px] font-medium uppercase tracking-wide text-[#6B7280] sticky left-0 bg-[#F8F9FC] z-10">Position</th>
              {MONTHS.map((m, i) => (
                <th key={i} className="text-right px-2 py-2 text-[11px] font-medium uppercase tracking-wide text-[#6B7280]">{m}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={13} className="px-4 py-8 text-center text-xs text-[#9CA3AF]">Lädt…</td></tr>
            ) : grid.length === 0 ? (
              <tr><td colSpan={13} className="px-4 py-8 text-center text-xs text-[#9CA3AF]">Keine Plan-Werte</td></tr>
            ) : grid.map(g => (
              <tr key={g.position} className="border-t border-[#F1F2F4] hover:bg-[#F8F9FC]/40">
                <td className="px-3 py-1.5 text-xs text-[#1A1A2E] sticky left-0 bg-white z-10 whitespace-nowrap align-top">
                  {labelize(g.position)}
                  {data?.treiber?.[g.position]?.aktiv && (
                    <span className="ml-1 text-[10px] text-[#6B7280]">
                      ({fmtEur(data.treiber[g.position].satz_eur, 2)} €/{MENGE_LABELS[data.treiber[g.position].menge_position]})
                    </span>
                  )}
                </td>
                {MONTHS.map((_, i) => (
                  <Zelle key={i} position={g.position} monat={i + 1} cell={g.cells[i + 1]} />
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mengenplanung (Teil B) */}
      <div className="bg-white border border-[#E5E7EB] rounded-xl overflow-x-auto">
        <div className="px-4 pt-3 text-xs font-semibold text-[#1A1A2E]">Mengenplanung je Monat (Forecast-Treiber)</div>
        <table className="text-sm min-w-full">
          <thead>
            <tr>
              <th className="text-left px-3 py-2 text-[11px] font-medium uppercase tracking-wide text-[#6B7280]">Menge</th>
              {MONTHS.map((m, i) => (
                <th key={i} className="text-right px-2 py-2 text-[11px] font-medium uppercase tracking-wide text-[#6B7280]">{m}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {Object.keys(MENGE_LABELS).map(mp => (
              <tr key={mp} className="border-t border-[#F1F2F4]">
                <td className="px-3 py-1.5 text-xs text-[#1A1A2E] whitespace-nowrap">{MENGE_LABELS[mp]} ({year})</td>
                {MONTHS.map((_, i) => {
                  const monat = i + 1;
                  const k = `${mp}|${monat}`;
                  const val = mengen[mp][monat];
                  return (
                    <td key={i} className="px-2 py-1 text-right tabular-nums text-xs">
                      {editKey === k ? (
                        <input
                          type="number" value={editValue} autoFocus disabled={saving}
                          onChange={e => setEditValue(e.target.value)}
                          onKeyDown={e => {
                            if (e.key === 'Enter') saveZelle(mp, monat);
                            if (e.key === 'Escape') setEditKey(null);
                          }}
                          onBlur={() => saveZelle(mp, monat)}
                          className="w-20 px-1 py-0.5 border border-[#338edc] rounded text-right text-xs"
                        />
                      ) : (
                        <button
                          disabled={readOnly}
                          onClick={() => { setEditKey(k); setEditValue(val ?? ''); }}
                          className={val === undefined ? 'text-[#C4C9D0] hover:text-[#338edc]' : 'text-[#1A1A2E] hover:text-[#338edc]'}
                        >
                          {val === undefined ? '–' : fmtEur(val)}
                        </button>
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Treiber-Verwaltung (Teil B) */}
      <div className="bg-white border border-[#E5E7EB] rounded-xl px-4 py-3 space-y-3">
        <div className="text-xs font-semibold text-[#1A1A2E]">Treiber: Kosten = Menge × € je Einheit</div>
        {Object.keys(data?.treiber || {}).length === 0 && (
          <div className="text-xs text-[#9CA3AF]">Kein Treiber hinterlegt — alle Forecast-Zeilen sind absolute Beträge.</div>
        )}
        {Object.entries(data?.treiber || {}).map(([pos, t]) => {
          const kz = kennzahlen[pos];
          return (
            <div key={pos} className="border border-[#F1F2F4] rounded-lg px-3 py-2 space-y-1">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="text-xs text-[#1A1A2E]">
                  <span className="font-medium">{labelize(pos)}</span>
                  {' = '}{MENGE_LABELS[t.menge_position]} × {fmtEur(t.satz_eur, 2)} €
                  {!t.aktiv && <span className="ml-2 text-[#9CA3AF]">(inaktiv)</span>}
                </div>
                {!readOnly && (
                  <button
                    onClick={async () => { if (await api('DELETE', `/api/investor/forecast/treiber/${encodeURIComponent(pos)}`)) load(); }}
                    className="text-xs text-[#DC2626] hover:underline"
                  >Entfernen</button>
                )}
              </div>
              {kz && (
                <div className="text-[11px] text-[#6B7280]">
                  Ist: {kz.monate.filter(m => m.eur_pro_einheit !== null).slice(-3).map(m => `${m.monat}: ${fmtEur(m.eur_pro_einheit, 2)} €/Einheit`).join(' · ')}
                  {kz.schnitt_3m !== null && <> · <span className="font-medium">Ø 3 Monate: {fmtEur(kz.schnitt_3m, 2)} €/Einheit</span></>}
                </div>
              )}
            </div>
          );
        })}
        {!readOnly && (
          <div className="flex items-center gap-2 flex-wrap">
            <select value={tf.position} onChange={e => setTf({ ...tf, position: e.target.value })} className="text-xs border border-[#E5E7EB] rounded-md px-2 py-1.5 bg-white">
              <option value="">Position wählen…</option>
              {grid.map(g => <option key={g.position} value={g.position}>{labelize(g.position)}</option>)}
            </select>
            <select value={tf.menge_position} onChange={e => setTf({ ...tf, menge_position: e.target.value })} className="text-xs border border-[#E5E7EB] rounded-md px-2 py-1.5 bg-white">
              {Object.keys(MENGE_LABELS).map(mp => <option key={mp} value={mp}>{MENGE_LABELS[mp]}</option>)}
            </select>
            <input
              type="number" step="0.01" placeholder="€ je Einheit" value={tf.satz_eur}
              onChange={e => setTf({ ...tf, satz_eur: e.target.value })}
              className="text-xs border border-[#E5E7EB] rounded-md px-2 py-1.5 w-28"
            />
            <button
              onClick={speichereTreiber} disabled={saving || !tf.position || tf.satz_eur === ''}
              className="text-xs px-3 py-1.5 rounded-md bg-[#338edc] text-white disabled:opacity-40"
            >Treiber setzen</button>
          </div>
        )}
      </div>

      {/* Historie-Overlay (AK3) */}
      {historie && (
        <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50" onClick={() => setHistorie(null)}>
          <div className="bg-white rounded-xl border border-[#E5E7EB] p-4 max-w-lg w-full mx-4 max-h-[70vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="text-sm font-semibold text-[#1A1A2E] mb-2">
              Versionen: {labelize(historie.position)} · {MONTHS[historie.monat - 1]} {year}
            </div>
            {historie.versionen.length === 0 ? (
              <div className="text-xs text-[#9CA3AF]">Noch keine Forecast-Version — der Plan gilt.</div>
            ) : (
              <table className="text-xs w-full">
                <thead>
                  <tr className="text-left text-[#6B7280]">
                    <th className="py-1 pr-2">Wert</th><th className="py-1 pr-2">Gültig von</th><th className="py-1 pr-2">Bis</th><th className="py-1">Von</th>
                  </tr>
                </thead>
                <tbody>
                  {historie.versionen.map((v, i) => (
                    <tr key={i} className="border-t border-[#F1F2F4]">
                      <td className="py-1 pr-2 tabular-nums text-[#1A1A2E]">{fmtEur(v.wert_eur)}</td>
                      <td className="py-1 pr-2">{new Date(v.gueltig_von).toLocaleString('de-AT')}</td>
                      <td className="py-1 pr-2">{v.gueltig_bis ? new Date(v.gueltig_bis).toLocaleString('de-AT') : 'aktuell'}</td>
                      <td className="py-1">{v.erstellt_von_email || '–'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            <div className="mt-3 text-right">
              <button onClick={() => setHistorie(null)} className="text-xs text-[#338edc] hover:underline">Schließen</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
